import React from "react";
import Link from 'next/link';

import {cn} from "@isudio/utils";
import {useRef} from "react";
import {useEffect} from "react";

export interface LinkItem {
  href?: string;
  label: string;
  handler: () => void;
}

interface FooterProps {
  className?: string;
  position?: "sticky" | "fixed";
  visible?: boolean;
  copyright?: string;
  links?: LinkItem[];
  onHeightChange?: (height: number) => void;
}

const Footer = React.forwardRef<Element, FooterProps>(
  ({
     className = "footer",
     position = "sticky",
     visible = false,
     copyright = `Copyright © 2005 - ${new Date().getFullYear()} iSudio.com. All rights reserved.`,
     links,
     onHeightChange
   }
  ) => {
    const footerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const resizeObserver = new ResizeObserver(() => {
        if (footerRef.current) {
          onHeightChange?.(footerRef.current.clientHeight);
        }
      });

      if (footerRef.current) {
        resizeObserver.observe(footerRef.current);
      }

      return () => resizeObserver.disconnect();
    }, [onHeightChange]);

    return (
      <footer
        ref={footerRef}
        className={cn(
          `${className}`, `footer w-full bottom-0 px-4 py-2 z-40 border-none ${position} bg-linear-to-t`,
          `${position === "fixed" ? "from-gray-darker/95 to-gray-medium/60" : "from-gray-darker to-gray-medium"}`,
          !visible
            ? "-translate-y-full opacity-0 pointer-events-none"
            : "translate-y-0 opacity-100"
        )}>
        <div className="flex lg:flex-row portrait:flex-col items-center justify-between text-center font-bold tracking-tight">
          <div className="flex gap-4 text-xs">
            {links ? links.map((l) => (
              <Link className="underline cursor-pointer transition duration-200 text-pale-white hover:text-generic-hover hover:scale-105" href={l?.href ? l?.href : "#"} onClick={l?.handler}>
                {l.label}
              </Link>
            )) : null}
          </div>

          <span
            className="text-xs text-pale-white italic">
            {copyright}
          </span>
        </div>
      </footer>
    )
  }
);

Footer.displayName = "Footer";
export {Footer};